/**
 * DayForm — Zentrales Tages-Eingabeformular (immer sichtbar im Stundenkonto-Screen).
 *
 * Lädt Daten für `date`, zeigt großzügige Form-Blöcke:
 *   - Projekt
 *   - Hinfahrt (Start/Ende/km)
 *   - Arbeitszeit (Start/Ende/Pause)
 *   - Rückfahrt (Start/Ende/km)
 *   - Unterkunft (auto aus Projekt + manuell)
 *   - Notiz
 *
 * Bearbeitungslogik:
 *   - Mitarbeiter: nur aktuelle ISO-Kalenderwoche
 *   - Admin/Projektleiter: immer
 *   - Außerhalb: Felder disabled + Hinweis
 *
 * Auto-Link:
 *   - Hinfahrt-Ende → Arbeitszeit-Start (wenn leer)
 *   - Arbeitszeit-Ende → Rückfahrt-Start (wenn leer)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { authFetch, useAuth } from "../App";
import { format, parseISO, startOfWeek, endOfWeek } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { toast } from "sonner";
import {
  Briefcase, Car, Clock, Bed, FileText, Save, Loader2, Lock,
  Hotel, Search, X, Check, AlertCircle, ChevronRight, ChevronLeft, Sparkles,
} from "lucide-react";
import useDailyContext from "../hooks/useDailyContext";
import { useTranslation } from "../i18n/LanguageProvider";

const API = "/api";

// date-fns hat kein „sk"-Bundle in unserer Installation — fallback auf de für SK (Datumsformat sehr ähnlich)
const LOCALE_MAP = { de, en: enUS, sk: de };

const DEFAULT_PAUSE_BASE = [0, 15, 30, 45, 60];

/**
 * Build PAUSE_OPTIONS dynamically based on user settings.
 * Always includes: 0 (Keine), user's configured pause_minutes, auto_pause_minutes (if >0), plus common values.
 */
function buildPauseOptions(user) {
  const set = new Set(DEFAULT_PAUSE_BASE);
  if (user?.pause_minutes != null && user.pause_minutes >= 0) set.add(user.pause_minutes);
  if (user?.auto_pause_minutes != null && user.auto_pause_minutes > 0) set.add(user.auto_pause_minutes);
  const sorted = Array.from(set).sort((a, b) => a - b);
  return sorted.map(v => ({ value: v, label: v === 0 ? "Keine" : `${v}` }));
}

const minutesBetween = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.max(0, mins);
};
const fmtHM = (mins) => `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;

const EMPTY_FORM = {
  project_id: "",
  hin_start: "", hin_end: "", hin_km: "", hin_reason: "",
  work_start: "", work_end: "", break_minutes: null,  // null = use user default
  rueck_start: "", rueck_end: "", rueck_km: "", rueck_reason: "",
  overnight_stay: false,
  accommodation: "",
  note: "",
};

// Standard-Vorschläge für Grund (Hin/Rückfahrt) — werden per t() übersetzt im Build der Optionen
const HIN_REASON_KEYS = [
  "df.reason_out_baustelle",
  "df.reason_out_hotel",
  "df.reason_out_material",
  "df.reason_out_other",
];
const RUECK_REASON_KEYS = [
  "df.reason_back_home",
  "df.reason_back_hotel",
  "df.reason_back_material",
  "df.reason_back_other",
];

function isInCurrentWeek(dateStr) {
  try {
    const d = parseISO(dateStr);
    const today = new Date();
    const ws = startOfWeek(today, { weekStartsOn: 1 });
    const we = endOfWeek(today, { weekStartsOn: 1 });
    return d >= ws && d <= we;
  } catch { return false; }
}

export default function DayForm({ date, weekDay, projects, projectsLoading, onPrevDay, onNextDay, canNext = true }) {
  const { user } = useAuth();
  const { t, lang } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [existing, setExisting] = useState(null);
  // Multi-Projekt pro Tag: alle Einträge des Tages + welcher gerade bearbeitet wird
  const [dayEntries, setDayEntries] = useState([]); // alle time_entries für das Datum
  const [dayTrips, setDayTrips] = useState([]);     // alle trips für das Datum
  const [editingProjectId, setEditingProjectId] = useState(null); // null = neuer Eintrag

  const isAdmin = (user?.role || "").toLowerCase() === "admin" || (user?.role || "").toLowerCase() === "projektleiter";
  const canEdit = isAdmin || isInCurrentWeek(date);

  // Daily-Context: Einsatzplanung + Unterkunft (zentrale Logik)
  const { context: dailyCtx } = useDailyContext(user?.id, date);
  const [autoApplied, setAutoApplied] = useState({ project: false, accommodation: false });

  // User-spezifische Pausen-Optionen aus Mitarbeitereinstellungen (pause_minutes + auto_pause_minutes + Standards)
  const pauseOptions = useMemo(() => buildPauseOptions(user), [user]);
  const userDefaultPause = user?.pause_minutes ?? 30;

  const isAbs = weekDay?.absence_type && weekDay.absence_type !== "arbeitstag";
  const absenceLabel = isAbs
    ? t({ urlaub: "df.abs_urlaub", krank: "df.abs_krank", feiertag: "df.abs_feiertag", frei: "df.abs_frei" }[weekDay.absence_type] || "")
    : null;

  /* ── Helper: lädt einen Eintrag + zugehörige Trips ins Form ── */
  const populateFormFromEntry = useCallback((entry, allTrips) => {
    const next = { ...EMPTY_FORM, break_minutes: userDefaultPause };
    if (entry) {
      next.project_id = entry.project_id || "";
      next.work_start = entry.work_start || "";
      next.work_end = entry.work_end || "";
      next.break_minutes = entry.break_minutes ?? userDefaultPause;
      next.overnight_stay = !!entry.overnight_stay;
      next.accommodation = entry.accommodation_name || "";
    }
    const projId = entry?.project_id || "";
    const projTrips = (allTrips || []).filter(t => (t.project_id || "") === projId);
    const hin = projTrips.find(t => ["wohnort_projekt", "unterkunft_projekt"].includes(t.type));
    const ruck = projTrips.find(t => ["projekt_unterkunft", "projekt_wohnort"].includes(t.type));
    const note = projTrips.find(t => t.type === "sonstiges" && t.note);
    if (hin) {
      next.hin_start = hin.start_time || "";
      next.hin_end = hin.end_time || "";
      next.hin_km = (hin.km ?? hin.kilometers) ?? "";
      next.hin_reason = hin.reason || "";
    }
    if (ruck) {
      next.rueck_start = ruck.start_time || "";
      next.rueck_end = ruck.end_time || "";
      next.rueck_km = (ruck.km ?? ruck.kilometers) ?? "";
      next.rueck_reason = ruck.reason || "";
    }
    if (note) next.note = note.note || "";
    if (!next.project_id && (hin?.project_id || ruck?.project_id)) {
      next.project_id = hin?.project_id || ruck?.project_id;
    }
    setForm(next);
  }, [userDefaultPause]);

  /* ── Load data when date changes ── */
  const loadDay = useCallback(async (opts = {}) => {
    if (!date) return;
    const keepProjectId = opts?.keepProjectId;
    setLoading(true);
    setAutoApplied({ project: false, accommodation: false });
    try {
      const [entryRes, tripsRes] = await Promise.all([
        authFetch(`${API}/time-entries?date_from=${date}&date_to=${date}`),
        authFetch(`${API}/trips?date=${date}`),
      ]);
      const entries = entryRes.ok ? await entryRes.json() : [];
      const tripsJson = tripsRes.ok ? await tripsRes.json() : null;
      const trips = Array.isArray(tripsJson) ? tripsJson : (tripsJson?.trips || []);

      // Nur Einträge für DIESES Datum (Backend hat alle returned; filter zur Sicherheit)
      const sameDay = (entries || []).filter(e => e.date === date);
      setDayEntries(sameDay);
      setDayTrips(trips);

      // Welcher Eintrag wird zuerst angezeigt? — bevorzugt der zuletzt gespeicherte
      let firstEntry = null;
      if (keepProjectId) {
        firstEntry = sameDay.find(e => (e.project_id || "") === keepProjectId) || null;
      }
      if (!firstEntry && sameDay.length > 0) firstEntry = sameDay[0];
      setExisting(firstEntry);
      setEditingProjectId(firstEntry?.project_id || null);
      populateFormFromEntry(firstEntry, trips);
    } catch (e) {
      console.error("[DayForm] load failed:", e);
    }
    setLoading(false);
  }, [date, populateFormFromEntry]);

  useEffect(() => { loadDay(); }, [loadDay]);

  /* ── Eintrag wechseln / neuer Eintrag / löschen ── */
  const switchToEntry = useCallback((projectId) => {
    const entry = dayEntries.find(e => (e.project_id || "") === (projectId || "")) || null;
    setExisting(entry);
    setEditingProjectId(entry?.project_id || null);
    populateFormFromEntry(entry, dayTrips);
  }, [dayEntries, dayTrips, populateFormFromEntry]);

  const startNewEntry = useCallback(() => {
    setExisting(null);
    setEditingProjectId(null);
    setForm({ ...EMPTY_FORM, break_minutes: userDefaultPause });
  }, [userDefaultPause]);

  const deleteEntry = useCallback(async (projectId) => {
    const entry = dayEntries.find(e => (e.project_id || "") === (projectId || "")) || null;
    const projTrips = (dayTrips || []).filter(t => (t.project_id || "") === (projectId || ""));
    const label = entry?.project_name || projects.find(p => p.id === projectId)?.name || t("df.section_project");
    if (!window.confirm(t("df.delete_confirm", { name: label }))) return;
    const tasks = [];
    if (entry?.id) tasks.push(authFetch(`${API}/time-entries/${entry.id}`, { method: "DELETE" }));
    for (const tr of projTrips) tasks.push(authFetch(`${API}/trips/${tr.id}`, { method: "DELETE" }));
    try {
      await Promise.allSettled(tasks);
      toast.success(t("df.toast_deleted"));
      // Zurück auf „neuer Eintrag" und neu laden
      setEditingProjectId(null);
      await loadDay();
    } catch {
      toast.error(t("df.toast_error_delete"));
    }
  }, [dayEntries, dayTrips, projects, loadDay, t]);

  /* ── Auto-Voraulswahl: Projekt aus Einsatzplanung + Unterkunft ── */
  useEffect(() => {
    if (!dailyCtx) return;
    if (loading) return;
    // Nicht überschreiben, wenn der User bereits einen Eintrag hat (existing) — der hat Vorrang.
    setForm(prev => {
      const next = { ...prev };
      let changedProject = false;
      let changedAcc = false;

      // 1) PROJEKT: nur setzen wenn aktuell leer UND Fall A (auto_project_id) gegeben
      if (!prev.project_id && dailyCtx.auto_project_id) {
        next.project_id = dailyCtx.auto_project_id;
        changedProject = true;
      }

      // 2) UNTERKUNFT: wenn passende Accommodation vorhanden UND noch nicht aktiv
      if (dailyCtx.accommodation && !prev.overnight_stay) {
        next.overnight_stay = true;
        if (!prev.accommodation) next.accommodation = dailyCtx.accommodation.name || "";
        changedAcc = true;
      }

      if (!changedProject && !changedAcc) return prev;
      setAutoApplied(a => ({
        project: a.project || changedProject,
        accommodation: a.accommodation || changedAcc,
      }));
      return next;
    });
  }, [dailyCtx, loading]);

  /* ── Live Berechnung ── */
  const workMinutes = Math.max(0, minutesBetween(form.work_start, form.work_end) - parseInt(form.break_minutes || 0, 10));
  const travelMinutes = minutesBetween(form.hin_start, form.hin_end) + minutesBetween(form.rueck_start, form.rueck_end);
  const firstTime = form.hin_start || form.work_start;
  const lastTime = form.rueck_end || form.work_end;
  const presenceMinutes = minutesBetween(firstTime, lastTime);
  const vma = presenceMinutes >= 24 * 60 ? 28 : presenceMinutes >= 8 * 60 ? 14 : 0;

  /* ── Auto-Unterkunft (lokal aus Projekt oder zentral aus Daily-Context) ── */
  const selectedProject = projects.find(p => p.id === form.project_id) || null;
  const matchedAccommodation = useMemo(() => {
    // 1. Zentrale Logik (Daily-Context) hat Vorrang — findet auch über Projekte hinweg
    if (dailyCtx?.accommodation) return dailyCtx.accommodation;
    // 2. Fallback: lokale Logik aus aktuell ausgewähltem Projekt
    if (!selectedProject || !user?.id) return null;
    const list = Array.isArray(selectedProject.accommodations) ? selectedProject.accommodations : [];
    if (list.length === 0) return null;
    return list.find(a => {
      if (!a) return false;
      const users = Array.isArray(a.assigned_user_ids) ? a.assigned_user_ids : [];
      if (users.length > 0 && !users.includes(user.id)) return false;
      const fromOk = !a.from_date || date >= a.from_date;
      const toOk = !a.to_date || date <= a.to_date;
      return fromOk && toOk;
    }) || null;
  }, [dailyCtx?.accommodation, selectedProject, user?.id, date]);

  /* ── Save ── */
  const handleSave = async () => {
    if (!form.project_id) { toast.error(t("df.toast_select_project")); return; }
    if (!form.work_start && !form.hin_start) {
      toast.error(t("df.toast_min_times"));
      return;
    }
    setSaving(true);
    const tasks = [];

    if (form.hin_start && form.hin_end) {
      const km = form.hin_km !== "" && form.hin_km != null ? Number(form.hin_km) : null;
      tasks.push(authFetch(`${API}/trips`, {
        method: "POST",
        body: JSON.stringify({
          type: "wohnort_projekt",
          start_time: form.hin_start, end_time: form.hin_end,
          km, kilometers: km,
          reason: form.hin_reason || "",
          date, project_id: form.project_id,
        }),
      }));
    }
    if (form.work_start && form.work_end) {
      tasks.push(authFetch(`${API}/time-entries/simple`, {
        method: "POST",
        body: JSON.stringify({
          date, project_id: form.project_id,
          work_start: form.work_start, work_end: form.work_end,
          break_minutes: parseInt(form.break_minutes || 0, 10),
          overnight_stay: form.overnight_stay,
          accommodation_name: form.accommodation || null,
          meal_allowance: vma,
        }),
      }));
    }
    if (form.rueck_start && form.rueck_end) {
      const km = form.rueck_km !== "" && form.rueck_km != null ? Number(form.rueck_km) : null;
      tasks.push(authFetch(`${API}/trips`, {
        method: "POST",
        body: JSON.stringify({
          type: "projekt_wohnort",
          start_time: form.rueck_start, end_time: form.rueck_end,
          km, kilometers: km,
          reason: form.rueck_reason || "",
          date, project_id: form.project_id,
        }),
      }));
    }
    if (form.note && form.note.trim()) {
      tasks.push(authFetch(`${API}/trips`, {
        method: "POST",
        body: JSON.stringify({
          type: "sonstiges",
          note: form.note.trim(),
          date, project_id: form.project_id,
        }),
      }));
    }

    try {
      const results = await Promise.allSettled(tasks);
      const failed = results.filter(r => r.status === "rejected" || (r.value && !r.value.ok));
      if (failed.length === 0) {
        toast.success(t("df.toast_saved"));
        // Auf dem soeben gespeicherten Projekt bleiben — loadDay würde sonst zum 1. Eintrag springen
        const savedProjectId = form.project_id;
        await loadDay({ keepProjectId: savedProjectId });
        window.dispatchEvent(new CustomEvent("stundenkonto-refresh"));
      } else {
        let errMsg = `${failed.length}/${results.length} ${t("df.toast_error_save")}`;
        for (const f of failed) {
          if (f.value && !f.value.ok) {
            try { const j = await f.value.json(); if (j.detail) { errMsg = j.detail; break; } } catch {}
          }
        }
        toast.error(errMsg);
      }
    } catch {
      toast.error(t("df.toast_error_save"));
    }
    setSaving(false);
  };

  const dateLabel = useMemo(() => {
    try { return format(parseISO(date), "EEEE, d. MMM yyyy", { locale: LOCALE_MAP[lang] || de }); } catch { return date; }
  }, [date, lang]);

  return (
    <div className="bg-white/[0.03] rounded-xl border border-white/8 p-4 space-y-3" data-testid="dayform">
      {/* Header — mit Vor/Zurück Navigation */}
      <div className="flex items-center justify-between pb-2 border-b border-white/8">
        <button
          type="button"
          onClick={onPrevDay}
          disabled={!onPrevDay}
          className="p-1.5 rounded-md hover:bg-white/[0.06] disabled:opacity-30 transition-colors"
          aria-label={t("df.prev_day")}
          data-testid="df-prev-day"
        >
          <ChevronLeft className="w-4 h-4 text-gray-400" />
        </button>
        <div className="text-center flex-1">
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{t("df.title")}</div>
          <div className="text-sm font-semibold text-white flex items-center justify-center gap-2" data-testid="dayform-date">
            {dateLabel}
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" />}
          </div>
        </div>
        <button
          type="button"
          onClick={onNextDay}
          disabled={!onNextDay || !canNext}
          className="p-1.5 rounded-md hover:bg-white/[0.06] disabled:opacity-30 transition-colors"
          aria-label={t("df.next_day")}
          data-testid="df-next-day"
        >
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {isAbs ? (
        <div className="py-6 text-center">
          <span className="px-3 py-1.5 rounded-md font-semibold text-sm" style={{
            backgroundColor: weekDay.absence_type === "urlaub" ? "rgba(251,191,36,0.15)" : weekDay.absence_type === "krank" ? "rgba(248,113,113,0.15)" : "rgba(96,165,250,0.15)",
            color: weekDay.absence_type === "urlaub" ? "#FBBF24" : weekDay.absence_type === "krank" ? "#F87171" : "#60A5FA",
          }}>
            {absenceLabel}
          </span>
          <div className="mt-2 text-[12px] text-gray-500">{t("df.no_workday")}</div>
        </div>
      ) : (
        <fieldset disabled={!canEdit || saving} className={!canEdit ? "opacity-60 space-y-2.5" : "space-y-2.5"}>
          {!canEdit && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-[13px]">
              <Lock className="w-4 h-4 shrink-0" />
              <span>{t("df.edit_only_current_week")}</span>
            </div>
          )}

          {/* PROJEKTE HEUTE — Pill-Bar (sichtbar wenn schon Einträge existieren) */}
          {dayEntries.length > 0 && (
            <div className="rounded-xl border border-blue-500/25 bg-gradient-to-br from-blue-600/15 to-blue-600/[0.04] p-3" data-testid="df-day-entries-list">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold text-blue-300 uppercase tracking-wider">
                  {t("df.projects_today")} ({dayEntries.length})
                </div>
                <div className="text-[10px] text-gray-400">{t("df.tap_to_edit")}</div>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {dayEntries.map(e => {
                  const isActive = (editingProjectId || "") === (e.project_id || "");
                  const label = e.project_name || projects.find(p => p.id === e.project_id)?.name || t("df.section_project");
                  const shortLabel = label.length > 22 ? label.slice(0, 20) + "…" : label;
                  return (
                    <div
                      key={e.id}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${
                        isActive
                          ? "bg-blue-500/30 border-blue-400/60 text-blue-50"
                          : "bg-white/5 border-white/10 text-gray-200 active:bg-white/10"
                      }`}
                      data-testid={`df-entry-pill-${e.project_id || "no-project"}`}
                    >
                      <button
                        type="button"
                        onClick={() => switchToEntry(e.project_id)}
                        className="flex items-center gap-1.5"
                        data-testid={`df-entry-pill-select-${e.project_id || "no-project"}`}
                      >
                        {isActive && <Check className="w-3 h-3" />}
                        <span>{shortLabel}</span>
                        {e.work_start && e.work_end && (
                          <span className="opacity-60 font-mono text-[10px]">{e.work_start}–{e.work_end}</span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); deleteEntry(e.project_id); }}
                        className="p-0.5 -mr-1 rounded-full hover:bg-white/10 active:bg-white/20"
                        title={t("common.delete", "Löschen")}
                        data-testid={`df-entry-pill-delete-${e.project_id || "no-project"}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={startNewEntry}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
                    editingProjectId === null
                      ? "bg-emerald-500/30 border-emerald-400/60 text-emerald-50"
                      : "bg-white/5 border-dashed border-white/20 text-gray-200 active:bg-white/10"
                  }`}
                  data-testid="df-entry-pill-new"
                >
                  {editingProjectId === null && <Check className="w-3 h-3" />}
                  {t("df.add_another_project")}
                </button>
              </div>
            </div>
          )}

          {/* PROJEKT — full-width, prominent */}
          <Block icon={Briefcase} title={t("df.section_project")} required tone="project">
            <ProjectCombobox
              projects={projects}
              loading={projectsLoading}
              value={form.project_id}
              onChange={(pid) => setForm(p => ({ ...p, project_id: pid }))}
              plannedProjectIds={(dailyCtx?.planned_projects || []).map(p => p.id)}
              disabled={!!existing}
              t={t}
            />
            {existing && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-400" data-testid="df-project-locked-hint">
                <Lock className="w-3 h-3" />
                <span>{t("df.project_locked")}</span>
              </div>
            )}
            {autoApplied.project && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-blue-300" data-testid="df-auto-project-hint">
                <Sparkles className="w-3 h-3" />
                <span>{t("df.project_auto_applied")}</span>
              </div>
            )}
            {dailyCtx && dailyCtx.planned_projects && dailyCtx.planned_projects.length > 1 && !autoApplied.project && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-300" data-testid="df-multi-project-hint">
                <AlertCircle className="w-3 h-3" />
                <span>{t("df.multi_projects_planned")}</span>
              </div>
            )}
          </Block>

          {/* Auto-Unterkunft — KOMPAKT */}
          {matchedAccommodation && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-500/30 bg-purple-500/[0.06]" data-testid="df-accommodation-detected">
              <Hotel className="w-4 h-4 shrink-0 text-purple-300" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 shrink-0">
                    {t("df.accommodation_label")}
                  </span>
                  <span className="text-[13px] text-gray-100 font-medium truncate">{matchedAccommodation.name}</span>
                </div>
                {matchedAccommodation.address && (
                  <div className="text-[11px] text-gray-400 truncate" style={{ pointerEvents: "none" }}>
                    {matchedAccommodation.address}
                  </div>
                )}
              </div>
              {!form.overnight_stay ? (
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, overnight_stay: true, accommodation: matchedAccommodation.name }))}
                  className="text-[11px] px-2 py-1 rounded-md bg-purple-500/25 hover:bg-purple-500/35 text-purple-100 font-semibold shrink-0"
                  data-testid="df-accommodation-apply"
                >
                  {t("df.accommodation_apply")}
                </button>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-semibold shrink-0 flex items-center gap-1">
                  <Check className="w-3 h-3" /> {t("df.accommodation_active")}
                </span>
              )}
            </div>
          )}

          {/* HINFAHRT */}
          <Block icon={Car} title={t("df.section_outbound")} tone="travel">
            <div className="grid grid-cols-2 gap-2.5">
              <TimePill label={t("df.field_start")} value={form.hin_start} onChange={v => setForm(p => ({ ...p, hin_start: v }))} testId="df-hin-start" />
              <TimePill
                label={t("df.field_end")}
                value={form.hin_end}
                onChange={v => setForm(p => {
                  const next = { ...p, hin_end: v };
                  if (v && !p.work_start) next.work_start = v;
                  return next;
                })}
                testId="df-hin-end"
              />
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-2.5">
              <KmPill value={form.hin_km} onChange={v => setForm(p => ({ ...p, hin_km: v }))} testId="df-hin-km" t={t} />
              <ReasonPill
                label={t("df.field_reason")}
                value={form.hin_reason}
                options={HIN_REASON_KEYS.map(k => t(k))}
                onChange={v => setForm(p => ({ ...p, hin_reason: v }))}
                testId="df-hin-reason"
                t={t}
              />
            </div>
          </Block>

          {/* ARBEITSZEIT — Highlight emerald */}
          <Block icon={Clock} title={t("df.section_work")} tone="work">
            <div className="grid grid-cols-2 gap-2.5">
              <TimePill label={t("df.field_start")} value={form.work_start} onChange={v => setForm(p => ({ ...p, work_start: v }))} testId="df-work-start" accent="emerald" />
              <TimePill
                label={t("df.field_end")}
                value={form.work_end}
                onChange={v => setForm(p => {
                  const next = { ...p, work_end: v };
                  if (v && !p.rueck_start) next.rueck_start = v;
                  return next;
                })}
                testId="df-work-end"
                accent="emerald"
              />
            </div>
            <div className="mt-2.5">
              <div className="text-[11px] font-semibold text-gray-400 mb-1.5">
                {t("df.field_break")} {user?.pause_minutes != null && <span className="text-gray-500">({t("df.break_default_hint", { min: user.pause_minutes })})</span>}
              </div>
              <div
                className="grid gap-1.5"
                style={{ gridTemplateColumns: `repeat(${Math.min(pauseOptions.length, 6)}, minmax(0, 1fr))` }}
              >
                {pauseOptions.map(o => {
                  const sel = parseInt(form.break_minutes, 10) === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, break_minutes: o.value }))}
                      className={`h-11 rounded-lg text-[13px] font-semibold transition-all ${
                        sel
                          ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20"
                          : "bg-black/30 text-gray-200 hover:bg-black/40 border border-white/15"
                      }`}
                      data-testid={`df-pause-${o.value}`}
                    >
                      {o.value === 0 ? t("df.break_none") : o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </Block>

          {/* RÜCKFAHRT */}
          <Block icon={Car} title={t("df.section_return")} tone="travel">
            <div className="grid grid-cols-2 gap-2.5">
              <TimePill label={t("df.field_start")} value={form.rueck_start} onChange={v => setForm(p => ({ ...p, rueck_start: v }))} testId="df-rueck-start" />
              <TimePill label={t("df.field_end")} value={form.rueck_end} onChange={v => setForm(p => ({ ...p, rueck_end: v }))} testId="df-rueck-end" />
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-2.5">
              <KmPill value={form.rueck_km} onChange={v => setForm(p => ({ ...p, rueck_km: v }))} testId="df-rueck-km" t={t} />
              <ReasonPill
                label={t("df.field_reason")}
                value={form.rueck_reason}
                options={RUECK_REASON_KEYS.map(k => t(k))}
                onChange={v => setForm(p => ({ ...p, rueck_reason: v }))}
                testId="df-rueck-reason"
                t={t}
              />
            </div>
          </Block>

          {/* ÜBERNACHTUNG */}
          <div className="bg-gradient-to-br from-purple-600/20 to-purple-600/5 border border-purple-500/25 rounded-xl overflow-hidden">
            <label className="flex items-center gap-3 px-3.5 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.overnight_stay}
                onChange={(e) => setForm(p => ({ ...p, overnight_stay: e.target.checked }))}
                className="w-5 h-5 accent-purple-500"
                data-testid="df-overnight"
              />
              <Bed className="w-4 h-4 text-purple-300" />
              <span className="text-[15px] font-medium text-gray-100">{t("df.section_overnight")}</span>
            </label>
            {form.overnight_stay && (
              <div className="px-3.5 pb-3.5">
                <input
                  type="text"
                  value={form.accommodation}
                  onChange={(e) => setForm(p => ({ ...p, accommodation: e.target.value }))}
                  placeholder={t("df.accommodation_placeholder")}
                  className="df-input text-[15px]"
                  data-testid="df-accommodation"
                />
              </div>
            )}
          </div>

          {/* NOTIZ */}
          <Block icon={FileText} title={t("df.section_note")} tone="note">
            <textarea
              value={form.note}
              onChange={(e) => setForm(p => ({ ...p, note: e.target.value }))}
              rows={3}
              placeholder={t("df.note_placeholder")}
              className="df-input resize-none text-[15px]"
              data-testid="df-note"
            />
          </Block>

          {/* SUMMARY — Sticky-ähnlich, gut sichtbar */}
          <div className="rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-3.5">
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">{t("df.summary_title")}</div>
            <div className="grid grid-cols-3 gap-2">
              <SummaryStat label={t("df.summary_work")} value={fmtHM(workMinutes)} color="#4ADE80" />
              <SummaryStat label={t("df.summary_travel")} value={fmtHM(travelMinutes)} color="#9CA3AF" />
              <SummaryStat label={t("df.summary_meal_allowance")} value={`${vma},00 €`} color="#FBBF24" />
            </div>
          </div>

          {/* SAVE — groß, mobile-friendly */}
          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !form.project_id}
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-base font-bold flex items-center justify-center gap-2.5 active:scale-[0.98] transition-all shadow-lg shadow-emerald-500/20"
              data-testid="df-save"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {existing ? t("df.btn_update") : t("df.btn_save")}
            </button>
          )}
        </fieldset>
      )}

      <style>{`
        .df-input {
          width: 100%;
          height: 48px;
          padding: 0 12px;
          border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(15,20,30,0.45);
          color: #FFFFFF;
          font-size: 15px;
          outline: none;
          transition: border-color 120ms, background 120ms;
        }
        .df-input::placeholder { color: rgba(255,255,255,0.55); }
        .df-input:focus {
          border-color: rgba(96,165,250,0.75);
          background: rgba(15,20,30,0.62);
        }
        textarea.df-input { height: auto; padding: 10px 12px; line-height: 1.4; }
        /* Glas-Modus (aktives Hintergrundbild): KEIN backdrop-filter — iOS rendert das milchig-weiß */
        html[data-has-bg] .df-input {
          background: rgba(15,20,30,0.55);
          border-color: rgba(255,255,255,0.25);
          color: #FFFFFF;
          box-shadow: 0 2px 10px rgba(0,0,0,0.35);
          text-shadow: 0 1px 1px rgba(0,0,0,0.45);
        }
        html[data-has-bg] .df-input::placeholder { color: rgba(255,255,255,0.70); }
        html[data-has-bg] .df-input:focus {
          background: rgba(15,20,30,0.72);
          border-color: rgba(96,165,250,0.85);
        }
      `}</style>
    </div>
  );
}

/* ── Sub-Components ── */
function Block({ icon: Icon, title, tone, required, children }) {
  // Farb-Schema wie auf dem Admin-Dashboard StatTile: transparenter Gradient + farbige Border
  // tone: project (blue), work (emerald), travel (slate), note (gray), overnight (purple)
  const tones = {
    project:   { grad: "from-blue-600/20 to-blue-600/5",       border: "border-blue-500/25",       icon: "text-blue-300" },
    work:      { grad: "from-emerald-600/20 to-emerald-600/5", border: "border-emerald-500/25",    icon: "text-emerald-300" },
    travel:    { grad: "from-slate-600/20 to-slate-600/5",     border: "border-slate-400/25",      icon: "text-slate-300" },
    note:      { grad: "from-gray-600/15 to-gray-600/5",       border: "border-gray-500/20",       icon: "text-gray-300" },
    overnight: { grad: "from-purple-600/20 to-purple-600/5",   border: "border-purple-500/25",     icon: "text-purple-300" },
  };
  const t = tones[tone] || tones.note;

  return (
    <div className={`bg-gradient-to-br ${t.grad} border ${t.border} rounded-xl p-3`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${t.icon}`} />
        <h3 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">
          {title}{required && <span className="text-red-400 ml-0.5">*</span>}
        </h3>
      </div>
      {children}
    </div>
  );
}

function TimePill({ label, value, onChange, testId, accent }) {
  const accentRing = accent === "emerald" ? "focus-within:border-emerald-500/60" : "focus-within:border-blue-500/55";
  return (
    <div>
      <label className="text-[11px] text-gray-300 block mb-1 font-semibold">{label}</label>
      <div className={`rounded-lg border border-white/15 bg-black/30 transition-all ${accentRing}`}>
        <input
          type="time" value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full h-12 px-3 bg-transparent text-[16px] font-mono text-white text-center outline-none rounded-lg"
          data-testid={testId}
        />
      </div>
    </div>
  );
}

function KmPill({ value, onChange, testId, t }) {
  return (
    <div>
      <label className="text-[11px] text-gray-300 block mb-1 font-semibold">{t ? t("df.field_kilometers") : "Kilometer"}</label>
      <div className="relative rounded-lg border border-white/15 bg-black/30 focus-within:border-blue-500/55 transition-all">
        <input
          type="number" inputMode="numeric" min="0" step="1"
          value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className="w-full h-12 px-3 pr-12 bg-transparent text-[16px] font-mono text-white outline-none rounded-lg"
          data-testid={testId}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-gray-300 font-semibold pointer-events-none">km</span>
      </div>
    </div>
  );
}

/**
 * ReasonPill — Grund-Auswahl mit Datalist (Free-Text + Vorschläge).
 * Sieht aus wie TimePill/KmPill, akzeptiert beliebigen Text.
 */
function ReasonPill({ label, value, options = [], onChange, testId, t }) {
  const datalistId = `${testId}-list`;
  return (
    <div>
      <label className="text-[11px] text-gray-300 block mb-1 font-semibold">{label}</label>
      <div className="rounded-lg border border-white/15 bg-black/30 focus-within:border-blue-500/55 transition-all">
        <input
          type="text"
          list={datalistId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t ? t("df.reason_placeholder") : "z.B. Fahrt zur Baustelle"}
          className="w-full h-12 px-3 bg-transparent text-[14px] text-white outline-none rounded-lg"
          data-testid={testId}
        />
        <datalist id={datalistId}>
          {options.map((o) => <option key={o} value={o} />)}
        </datalist>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, color }) {
  return (
    <div className="text-center">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">{label}</div>
      <div className="text-[15px] font-mono font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function ProjectCombobox({ projects, loading, value, onChange, plannedProjectIds = [], disabled = false, t }) {
  const tr = t || ((k) => k);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  // Mobile = < 768px: Bottom Sheet via Portal, sonst Desktop-Dropdown
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const selected = projects.find(p => p.id === value) || null;
  const plannedSet = useMemo(() => new Set(plannedProjectIds || []), [plannedProjectIds]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? projects.filter(p => {
          const hay = [p.name, p.short_code, p.project_number, p.display_name].filter(Boolean).join(" ").toLowerCase();
          return hay.includes(q);
        })
      : projects;
    // Geplante Projekte für diesen Tag nach oben sortieren
    return [...base].sort((a, b) => {
      const ap = plannedSet.has(a.id) ? 0 : 1;
      const bp = plannedSet.has(b.id) ? 0 : 1;
      return ap - bp;
    });
  }, [projects, query, plannedSet]);

  // Desktop-Dropdown: click-outside zum Schließen.
  // Mobile-Sheet: schließt nur via Overlay-Tap oder Close-Button.
  useEffect(() => {
    if (!open || isMobile) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, isMobile]);

  // Body-Scroll sperren, solange Bottom-Sheet offen ist
  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, isMobile]);

  if (!loading && projects.length === 0) {
    return (
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-300 text-[12px]">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{tr("df.no_projects_assigned")}</span>
      </div>
    );
  }

  const onSelect = (pid) => {
    onChange(pid);
    setOpen(false);
    setQuery("");
  };

  const trigger = (
    <button
      type="button" onClick={() => !disabled && setOpen(o => !o)}
      disabled={disabled}
      className={`df-input flex items-center justify-between text-left ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
      data-testid="df-project-trigger"
    >
      {loading ? (
        <span className="flex items-center gap-1.5 text-gray-400 text-[12px]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {tr("df.loading_projects")}
        </span>
      ) : selected ? (
        <span className="flex items-center gap-2 truncate">
          {selected.short_code && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-300 shrink-0">
              {selected.short_code}
            </span>
          )}
          <span className="truncate text-gray-100">{selected.name}</span>
        </span>
      ) : (
        <span className="text-gray-500">{tr("df.select_project")} ({projects.length})</span>
      )}
      <ChevronRight className={`w-3.5 h-3.5 text-gray-500 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
    </button>
  );

  // ====== MOBILE: BOTTOM SHEET via PORTAL ======
  if (isMobile) {
    const sheet = open && typeof document !== "undefined" ? createPortal(
      <div
        className="fixed inset-0 z-[9999] flex flex-col"
        data-testid="df-project-sheet"
        role="dialog"
        aria-modal="true"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="df-project-sheet-backdrop"
        />
        {/* Sheet */}
        <div className="relative mt-auto w-full bg-[#0e1320] rounded-t-2xl border-t border-white/10 shadow-2xl flex flex-col"
             style={{ maxHeight: "92vh", paddingBottom: "env(safe-area-inset-bottom, 0)" }}>
          {/* Drag handle */}
          <div className="pt-2 pb-1 flex justify-center">
            <div className="w-10 h-1 rounded-full bg-white/20" />
          </div>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
            <h3 className="text-sm font-semibold text-gray-100">{tr("df.select_project")}</h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-2 -m-2 rounded-full hover:bg-white/5 active:bg-white/10"
              data-testid="df-project-sheet-close"
              aria-label={tr("common.cancel", "Abbrechen")}
            >
              <X className="w-5 h-5 text-gray-300" />
            </button>
          </div>
          {/* Search */}
          <div className="relative border-b border-white/10">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              autoFocus type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={tr("common.search", "Suchen…")}
              className="w-full h-12 pl-10 pr-10 bg-transparent text-base text-gray-100 placeholder-gray-500 outline-none"
              data-testid="df-project-search"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-white/5 rounded">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
          {/* List */}
          <div className="overflow-y-auto flex-1" style={{ WebkitOverflowScrolling: "touch" }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-gray-500">{tr("df.no_match")}</div>
            ) : filtered.map(p => {
              const isSelected = p.id === value;
              const isPlanned = plannedSet.has(p.id);
              return (
                <button
                  key={p.id} type="button"
                  onClick={() => onSelect(p.id)}
                  className={`w-full px-4 py-3.5 flex items-center gap-2 text-left text-sm border-b border-white/[0.04] active:bg-white/[0.08] ${isSelected ? "bg-blue-500/10" : ""}`}
                  data-testid={`df-project-option-${p.id}`}
                >
                  {p.short_code && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-300 shrink-0">
                      {p.short_code}
                    </span>
                  )}
                  <span className="truncate flex-1 text-gray-100">{p.name}</span>
                  {isPlanned && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-emerald-500/20 text-emerald-300 shrink-0 flex items-center gap-0.5">
                      <Sparkles className="w-2.5 h-2.5" /> {tr("df.planned_today")}
                    </span>
                  )}
                  {isSelected && <Check className="w-4 h-4 text-blue-400 shrink-0" />}
                </button>
              );
            })}
          </div>
          {/* Cancel button */}
          <div className="border-t border-white/10 p-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full h-11 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium text-gray-200"
              data-testid="df-project-sheet-cancel"
            >
              {tr("common.cancel", "Abbrechen")}
            </button>
          </div>
        </div>
      </div>,
      document.body
    ) : null;

    return (
      <div ref={wrapRef} className="relative">
        {trigger}
        {sheet}
      </div>
    );
  }

  // ====== DESKTOP: ABSOLUTE DROPDOWN (unverändert) ======
  return (
    <div ref={wrapRef} className="relative">
      {trigger}
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-white/10 bg-[#0e1320] shadow-2xl overflow-hidden">
          <div className="relative border-b border-white/10">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              autoFocus type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={tr("common.search", "Suchen…")}
              className="w-full h-10 pl-9 pr-9 bg-transparent text-sm text-gray-100 placeholder-gray-500 outline-none"
              data-testid="df-project-search"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/5 rounded">
                <X className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-5 text-center text-[12px] text-gray-500">{tr("df.no_match")}</div>
            ) : filtered.map(p => {
              const isSelected = p.id === value;
              const isPlanned = plannedSet.has(p.id);
              return (
                <button
                  key={p.id} type="button"
                  onClick={() => onSelect(p.id)}
                  className={`w-full px-3 py-2 flex items-center gap-2 text-left text-sm hover:bg-white/[0.06] ${isSelected ? "bg-blue-500/10" : ""}`}
                  data-testid={`df-project-option-${p.id}`}
                >
                  {p.short_code && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-300 shrink-0">
                      {p.short_code}
                    </span>
                  )}
                  <span className="truncate flex-1 text-gray-100">{p.name}</span>
                  {isPlanned && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-emerald-500/20 text-emerald-300 shrink-0 flex items-center gap-0.5">
                      <Sparkles className="w-2.5 h-2.5" /> {tr("df.planned_today")}
                    </span>
                  )}
                  {isSelected && <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
